import * as Yup from "yup";
import {
  Box,
  Container,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import Select from "react-select";
import { Log, businessEntityAction, getCountryList, getEntityFilterConditions } from "../../services/services";
import { trimValues } from "../../utils/appUtils";
import FormDialog from "../../components/FormDialog";
import { urlRegex, INTERNATIONAL_COUNTRY_LIST, MAPPED_REGISTERED_COUNTRY, RISK_LEVEL } from "../../utils/constants";
import CustomDatePicker from "../../components/form/CustomDatePicker";
import moment from "moment";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import metadata from 'libphonenumber-js/metadata.full.json'
import './PhoneInput.css';


// use API to convert code ISO -> country name
const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const PRIORITY_CODES = ["SG", "MY", "TH"];
const allCountries = getCountries(metadata)
  .map((code) => ({
    code,
    label: regionNames.of(code),
    dialCode: "+" + getCountryCallingCode(code, metadata),
  }))
  // exclude +247
  .filter((country) => country.dialCode !== "+247");

// Split priority 
const priorityCountries = PRIORITY_CODES.map((pCode) =>
  allCountries.find((c) => c.code === pCode)
).filter(Boolean);
const otherCountries = allCountries.filter(
  (c) => !PRIORITY_CODES.includes(c.code)
);

// Join priority countries and others
export const COUNTRIES = [...priorityCountries, ...otherCountries];



const CreateNewBusinessEntity = ({ open, onClose }) => {
  const [countryList, setCountryList] = useState([]);
  const [entityFilterConditions, setEntityFilterConditions] = useState([]);
  const [_, setEntityFilterConditionsLoaded] = useState(false);

  useEffect(() => {
    getCountryList(setCountryList);
    getEntityFilterConditions(setEntityFilterConditions, setEntityFilterConditionsLoaded);
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    const newValues = trimValues({
      ...values,
      last_kyc_review: values.last_kyc_review ? new Date(values.last_kyc_review).toISOString() : null,
      next_kyc_review: values.next_kyc_review ? new Date(values.next_kyc_review).toISOString() : null,
    })

    Log.info(`Click button [Submit] - Create A New Business Entity`, { ...newValues });
    await businessEntityAction("ADD", newValues, onClose);
    setSubmitting(false);
  };

  return (
    <Container>
      <Box>
        <Formik
          initialValues={{
            business_name: "",
            short_name: "",
            uen: "",
            address_1: "",
            address_2: "",
            address_3: "",
            contact_name: "",
            contact_number: "+65",
            contact_email: "",
            registered_country: "",
            postal_code: "",
            address_country: "",
            gdrive_link: "",
            merchant_payout: [],
            bank_accounts: [],
            last_kyc_review: "",
            next_kyc_review: "",
            risk_level: ""
          }}
          onSubmit={handleSubmit}
          validationSchema={Yup.object().shape({
            business_name: Yup.string().required("Required").trim(),
            // short_name: Yup.string().required("Short name is required").trim(),
            uen: Yup.string()
              .required("Required").matches(/^[a-zA-Z0-9]{1,14}$/, 'Only Alphabets and numbers are allowed')
              .max(14, "Length of Registered ID should be 14 alphanumeric characters")
              .min(
                3,
                "Length of Registered ID should be at least 3 alphanumeric characters"
              ),
            registered_country: Yup.string().required("Required"),
            address_1: Yup.string().required("Required").trim(),
            address_2: Yup.string().required("Required").trim(),
            contact_name: Yup.string().required("Required").trim(),
            contact_email: Yup.string().email().required("Required").trim(),
            // contact_number: Yup.string().required("Required").trim(),
            // contact_number: Yup.string()
            // .required("Required")
            // .test("is-valid-phone", "Invalid phone number", (value) => {
            //   if (!value) return false;
            //   try {
            //     const phoneNumber = parsePhoneNumberFromString(value);
            //     return phoneNumber?.isValid() || false;
            //   } catch {
            //     return false;
            //   }
            // }),
            gdrive_link: Yup.string().nullable().matches(urlRegex, "Please enter a valid link"),
            merchant_payout: Yup.array().of(
              Yup.object().shape({
                id: Yup.string().required(),
                label: Yup.string().required()
              })
            ).required("Merchant Payout is required"),
            bank_accounts: Yup.array().of(
              Yup.object().shape({
                id: Yup.string().required(),
                label: Yup.string().required()
              })
            ).min(1, "Required"),
            last_kyc_review: Yup.string().nullable().when("next_kyc_review", {
              is: (val) => !!val,
              then: (schema) => schema.test("check", "Last KYC cannot be after Next KYC", function (val) {
                const { last_kyc_review, next_kyc_review } = this.parent;

                if (!last_kyc_review || !next_kyc_review) return true;
                return !(moment(last_kyc_review).isAfter(moment(next_kyc_review), 'day'))
              })
            }),
            next_kyc_review: Yup.string().nullable(),
            risk_level: Yup.string(),
          })}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              isSubmitting,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            } = props;
            return (
              <FormDialog
                open={open}
                onClose={onClose}
                title="Create A New Business Entity"
                scroll="paper"
                handleSubmit={handleSubmit}
                loading={isSubmitting}
              >
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                  <Grid
                    container
                    spacing={4}
                    sx={{ py: 2, px: 2 }}
                  >
                    <Grid item sm={6} xs={12}>
                      <Typography variant="h6" my={1}>
                        Business Registration
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                          <Typography align="right">Registered Name*</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Registered Name"
                            margin="dense"
                            required
                            fullWidth
                            id="business_name"
                            name="business_name"
                            size="small"
                            color="secondary"
                            value={values.business_name}
                            onChange={(e) => {
                              const uppercasedValue = e.target.value.toUpperCase();
                              setFieldValue("business_name", uppercasedValue)
                            }}
                            onBlur={handleBlur}
                            error={Boolean(
                              touched.business_name && errors.business_name
                            )}
                            helperText={
                              touched.business_name && errors.business_name
                            }
                            disabled={isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography align="right">Registered ID*</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Registered ID"
                            margin="dense"
                            required
                            fullWidth
                            id="uen"
                            name="uen"
                            size="small"
                            color="secondary"
                            value={values.uen}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={Boolean(touched.uen && errors.uen)}
                            helperText={touched.uen && errors.uen}
                            disabled={isSubmitting}
                            inputProps={{ maxLength: 14 }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography align="right">
                            Registered Country*
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          {countryList && (
                            <Autocomplete
                              options={countryList}
                              getOptionLabel={(option) => MAPPED_REGISTERED_COUNTRY[option.registered_country]}
                              value={countryList.find(option => option.id === values.registered_country) || null}
                              onChange={(event, newValue) => {
                                setFieldValue("registered_country", newValue ? newValue.id : "");
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Registered Country"
                                  size="small"
                                  margin="dense"
                                  error={Boolean(touched.registered_country && errors.registered_country)}
                                  helperText={touched.registered_country && errors.registered_country}
                                  disabled={isSubmitting}
                                />
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                            />
                          )}
                        </Grid>
                      </Grid>

                      {/* Registered Address */}
                      <Typography variant="h6" my={1} mt={2}>
                        Registered Address
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                          <Typography align="right">Address 1*</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Address 1"
                            margin="dense"
                            required
                            fullWidth
                            id="address_1"
                            name="address_1"
                            size="small"
                            color="secondary"
                            value={values.address_1}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={Boolean(touched.address_1 && errors.address_1)}
                            helperText={touched.address_1 && errors.address_1}
                            disabled={isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography align="right">Address 2*</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Address 2"
                            required
                            fullWidth
                            id="address_2"
                            name="address_2"
                            size="small"
                            color="secondary"
                            value={values.address_2}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={Boolean(touched.address_2 && errors.address_2)}
                            helperText={touched.address_2 && errors.address_2}
                            disabled={isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography align="right">Address 3</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Address 3"
                            margin="dense"
                            fullWidth
                            id="address_3"
                            name="address_3"
                            size="small"
                            color="secondary"
                            value={values.address_3}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={Boolean(touched.address_3 && errors.address_3)}
                            helperText={touched.address_3 && errors.address_3}
                            disabled={isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography align="right">Postal Code</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Postal Code"
                            fullWidth
                            name="postal_code"
                            size="small"
                            margin="dense"
                            value={values.postal_code}
                            onChange={handleChange}
                            disabled={isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography align="right">Country</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Autocomplete
                            options={INTERNATIONAL_COUNTRY_LIST}
                            getOptionLabel={(option) => option.name}
                            value={INTERNATIONAL_COUNTRY_LIST.find(option => option.name === values.address_country?.toUpperCase()) || null}
                            onChange={(event, newValue) => {
                              setFieldValue("address_country", newValue ? newValue.name : "");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Country"
                                size="small"
                                margin="dense"
                                error={Boolean(touched.address_country && errors.address_country)}
                                helperText={touched.address_country && errors.address_country}
                                disabled={isSubmitting}
                              />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item sm={6} xs={12}>
                      <Typography variant="h6" my={1}>
                        Official Contact
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                          <Typography align="right">Name*</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Name"
                            margin="dense"
                            fullWidth
                            id="contact_name"
                            name="contact_name"
                            size="small"
                            color="secondary"
                            value={values.contact_name}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={Boolean(
                              touched.contact_name && errors.contact_name
                            )}
                            helperText={
                              touched.contact_name && errors.contact_name
                            }
                            disabled={isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography align="right">Contact Number</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Autocomplete
                            options={COUNTRIES}
                            autoHighlight
                            // Input only show dialCode (If option were object)
                            getOptionLabel={(option) =>
                              typeof option === "string" ? option : option.dialCode
                            }
                            value={
                              COUNTRIES.find((c) =>
                                values.contact_number?.startsWith(c.dialCode)
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              if (newValue) {
                                setFieldValue("contact_number", newValue.dialCode);
                              }
                            }}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <img
                                  loading="lazy"
                                  width="20"
                                  src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                  srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                  alt=""
                                  style={{ marginRight: 8 }}
                                />
                                {option.label} ({option.dialCode})
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                className="phone-textfield"
                                label="Contact Number"
                                margin="dense"
                                fullWidth
                                id="contact_number"
                                name="contact_number"
                                size="small"
                                color="secondary"
                                value={values.contact_number}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                // error={Boolean(touched.contact_number && errors.contact_number)}
                                // helperText={
                                //   touched.contact_number && errors.contact_number
                                //     ? errors.contact_number
                                //     : ""
                                // }
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment
                                      position="start"
                                      className="phone-flag-adornment"
                                    >
                                      {(() => {
                                        const selected = COUNTRIES.find((c) =>
                                          values.contact_number?.startsWith(c.dialCode)
                                        );
                                        return selected ? (
                                          <img
                                            className="phone-flag-img"
                                            src={`https://flagcdn.com/w20/${selected.code.toLowerCase()}.png`}
                                            srcSet={`https://flagcdn.com/w40/${selected.code.toLowerCase()}.png 2x`}
                                            alt={selected.code}
                                          />
                                        ) : null;
                                      })()}
                                    </InputAdornment>
                                  ),
                                }}
                                disabled={isSubmitting}
                              />
                            )}
                            freeSolo
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography align="right">Email*</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Email"
                            margin="dense"
                            fullWidth
                            id="contact_email"
                            name="contact_email"
                            size="small"
                            color="secondary"
                            value={values.contact_email}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={Boolean(
                              touched.contact_email && errors.contact_email
                            )}
                            helperText={
                              touched.contact_email && errors.contact_email
                            }
                            disabled={isSubmitting}
                          />
                        </Grid>
                      </Grid>

                      <Typography variant="h6" my={1}>
                        More Details
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                          <Typography align="right">Data Folder</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Data Folder"
                            margin="dense"
                            fullWidth
                            id="gdrive_link"
                            name="gdrive_link"
                            size="small"
                            color="secondary"
                            multiline
                            maxRows={10}
                            value={values.gdrive_link}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={Boolean(
                              touched.gdrive_link && errors.gdrive_link
                            )}
                            helperText={
                              touched.gdrive_link && errors.gdrive_link
                            }
                            disabled={isSubmitting}
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} alignItems="center" mt={1}>
                        <Grid item xs={4}>
                          <Typography align="right">Merchant Payout</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Select
                            id={"merchant_payout"}
                            name={"merchant_payout"}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.id}
                            closeMenuOnSelect={false}
                            isMulti
                            options={
                              entityFilterConditions?.merchant_payout?.filter(item => item && item.merchant_id && item.merchant_name)
                                .map((item) => ({
                                  id: item.merchant_id,
                                  label: `${item.merchant_name} (${item.source})`,
                                  source: item.source,
                                })) || []
                            }
                            value={values.merchant_payout || []}
                            onChange={(event) => setFieldValue("merchant_payout", event)}
                            styles={{
                              control: (provided) => ({
                                ...provided,
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                            }}
                            menuPortalTarget={document.body}
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} alignItems="center" mt={1}>
                        <Grid item xs={4}>
                          <Typography align="right">Bank Account*</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Autocomplete
                            multiple
                            id="bank_accounts"
                            options={
                              entityFilterConditions?.bank_accounts?.map((item) => ({
                                id: item.id,
                                label: `${item.account_number} (${item.bank})`,
                              })) || []
                            }
                            getOptionLabel={(option) => option.label}
                            value={values.bank_accounts || []}
                            onChange={(event, newValue) => {
                              setFieldValue("bank_accounts", newValue);
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            disabled={isSubmitting}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Bank Account"
                                margin="dense"
                                size="small"
                                color="secondary"
                                error={Boolean(touched.bank_accounts && errors.bank_accounts)}
                                helperText={touched.bank_accounts && errors.bank_accounts}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      <Typography variant="h6">
                        KYC
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                          <Typography align="right">Last KYC review</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <CustomDatePicker
                            inputFormat="DD/MM/YYYY"
                            value={values.last_kyc_review || ""}
                            onChange={(newValue) => {
                              setFieldValue("last_kyc_review", newValue);
                            }}
                            helperText={touched.last_kyc_review && errors.last_kyc_review}
                            error={Boolean(touched.last_kyc_review && errors.last_kyc_review)}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2} alignItems="center" mt={2}>
                        <Grid item xs={4}>
                          <Typography align="right">Next KYC review</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <CustomDatePicker
                            inputFormat="DD/MM/YYYY"
                            value={values.next_kyc_review || ""}
                            onChange={(newValue) => {
                              setFieldValue("next_kyc_review", newValue);
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2} alignItems="center" mt={1}>
                        <Grid item xs={4}>
                          <Typography align="right">Risk Level</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Autocomplete
                            options={RISK_LEVEL}
                            getOptionLabel={(option) => option.label}
                            value={RISK_LEVEL.find(option => option.id === values.risk_level) || null}
                            onChange={(event, newValue) => {
                              setFieldValue("risk_level", newValue ? newValue.id : "");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Risk Level"
                                size="small"
                                margin="dense"
                                error={Boolean(touched.risk_level && errors.risk_level)}
                                helperText={touched.risk_level && errors.risk_level}
                                disabled={isSubmitting}
                              />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                          />
                        </Grid>
                      </Grid>

                    </Grid>
                  </Grid>
                </Form>
              </FormDialog>
            );
          }}
        </Formik>
      </Box>
    </Container>
  );
};

export default CreateNewBusinessEntity;
